import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { CONTACT, getWhatsAppUrl, getPhoneLink } from '../constants/contact';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const prefilledType = searchParams.get('type') || '';
  const prefilledPlan = searchParams.get('plan') || '';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: prefilledType || '',
    plan: prefilledPlan || '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          inquiry_type: formData.inquiryType,
          message: formData.message || null,
          package_selected: formData.plan || null
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      navigate('/thank-you');
    } catch (err) {
      setError('Unable to submit. Please try again or contact us directly.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <SEO
        title="Contact"
        description="Get in touch with The Mundalo for cross-border real estate advisory, relocation services, and investment opportunities in Mexico."
        path="/contact"
      />
      <Header currentPage="contact" />

      {/* Contact Form */}
      <section className="section">
        <div className="container-narrow mx-auto">
          <div className="text-center mb-12">
            <h1 className="heading-xl mb-6">{t('contact.title')}</h1>
            <p className="body-lg">{t('contact.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 text-sm">
                {error}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('contact.name')} *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                  placeholder={t('contact.yourName')}
                  data-testid="contact-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('contact.email')} *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                  placeholder={t('contact.yourEmail')}
                  data-testid="contact-email"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('contact.phone')}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                  placeholder={t('contact.phonePlaceholder')}
                  data-testid="contact-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('contact.inquiryType')} *</label>
                <select
                  required
                  value={formData.inquiryType}
                  onChange={(e) => setFormData({...formData, inquiryType: e.target.value})}
                  className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                  data-testid="contact-inquiry-type"
                >
                  <option value="">{t('contact.selectType')}</option>
                  <option value="development">{t('contact.developmentProject')}</option>
                  <option value="relocation">{t('contact.relocation')}</option>
                  <option value="buyer">{t('contact.buyerRepresentation')}</option>
                  <option value="general">{t('contact.general')}</option>
                </select>
              </div>
            </div>

            {formData.inquiryType === 'relocation' && formData.plan && (
              <div className="bg-[#FFFFFF] border border-[#E0D6B8] p-4">
                <p className="text-sm font-medium mb-1 text-[#0A1F2E]">{t('contact.selectedPlan')}:</p>
                <p className="body-md">{formData.plan.replace('RELO-', '').replace('-', ' ')}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 text-[#0A1F2E]">{t('contact.message')} *</label>
              <textarea
                required
                rows="6"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 border border-[#E0D6B8] bg-[#FFFFFF] text-[#0A1F2E]"
                placeholder={t('contact.messagePlaceholder')}
                data-testid="contact-message"
              />
            </div>

            <div className="text-center">
              <button 
                type="submit" 
                className="btn-primary disabled:opacity-50"
                disabled={isSubmitting}
                data-testid="contact-submit"
              >
                {isSubmitting ? t('contact.sending') : t('contact.sendInquiry')}
              </button>
            </div>

            <p className="body-sm text-center">{t('contact.responseTime')}</p>
          </form>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-sm bg-[#FFFFFF]">
        <div className="container-narrow mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="label mb-2">{t('contact.emailLabel')}</p>
              <a href={`mailto:${CONTACT.email}`} className="body-md hover:text-[#2CA8A0] transition">
                {CONTACT.email}
              </a>
            </div>
            <div>
              <p className="label mb-2">{t('contact.phoneUS')}</p>
              <a href={getPhoneLink('us')} className="body-md hover:text-[#2CA8A0] transition">
                {CONTACT.phone.us}
              </a>
            </div>
            <div>
              <p className="label mb-2">{t('contact.whatsAppMX')}</p>
              <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="body-md hover:text-[#2CA8A0] transition">
                {CONTACT.phone.mx}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
